<template>
  <div class="d-flex flex-column mb-2 output-group">
    <div
      class="d-flex flex-column flex-lg-row flex-wrap align-items-start align-items-lg-center justify-content-between"
    >
      <div class="text-md mb-1 output-group__heading">
        <div class="text-thicker">
          {{ group.name }} {{ group.customName ? `- ${group.customName}` : '' }}
        </div>
        <div
          v-if="group.typeDescription"
          class="text-secondary output-group-path text-sm"
        >
          Type:
          <span class="text-main">
            {{ group.typeDescription }}
          </span>
        </div>
        <div
          v-if="group.uploadedPath"
          class="text-secondary output-group-path text-sm"
        >
          Path:
          <a
            v-clipboard:copy="outputGroupPathComputed"
            v-clipboard:success="copySuccess"
            class="text-primary text-break output-group__path-link"
          >
            <span class="output-group__path-text">
              {{ outputGroupPathComputed }}
            </span>
            <GjIcon
              name="Copy"
              size="20"
            />
          </a>
        </div>
      </div>
      <b-button
        v-if="!isEnded"
        variant="bg-white"
        class="mb-1"
        :disabled="refreshing"
        @click="refresh"
      >
        <GjIcon
          name="Refresh"
          class="icon-left"
        />
        Refresh
      </b-button>
    </div>

    <div class="d-lg-none">
      <div
        v-if="refreshing && !tableData.length"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <template v-else-if="tableData.length">
        <div :class="{ 'opacity-7': refreshing }">
          <output-mobile-card
            v-for="output in tableData"
            :key="output.id"
            :output="output"
            :is-hls="isHLS"
            :show-transcriptions="isOutputMp3"
            :endpoint="job.destinationEndpoint"
            :destination="destination"
            :group="group"
            @show-logs="showLogs"
            @restart="restartEncodingOutput"
          />
        </div>
      </template>
      <div
        v-else
        class="output-group__empty"
      >
        No outputs in this group yet.
      </div>
    </div>

    <div class="d-none d-lg-block">
      <b-table
        ref="table"
        :items="tableData"
        :fields="filteredFields"
        show-empty
        responsive
        class="loaded border-default border-b border-r border-l rounded"
        :busy="refreshing"
      >
        <template #cell(name)="data">
          <div class="d-flex flex-column">
            <div v-if="!isHLS">
              {{ data.item.nameModifier || data.value }}
            </div>
            <div v-else>
              {{ `${data.value} - ${data.item.nameModifier}` }}
            </div>

            <a
              v-if="data.item.path"
              v-clipboard:copy="data.item.path"
              v-clipboard:success="copySuccess"
              class="text-primary text-sm text-truncate"
            >
              <span
                v-b-tooltip.hover.v-light.topright="{
                  customClass: 'link-tooltip',
                  title: data.item.path,
                }"
              >
                {{ data.item.path | urlLimit }}
              </span>
            </a>
          </div>
        </template>

        <template #row-details="row">
          <b-card>
            <job-transcriptions
              :table-data="row.item.transcribeJob"
              :endpoint="job.destinationEndpoint"
              :destination="destination"
              :group="group"
              @closeTable="row.toggleDetails"
            />
          </b-card>
        </template>

        <template #cell(transcriptions)="row">
          <div v-if="row.item.transcribeJob === null">
            -
          </div>
          <div v-else>
            <span class="d-flex align-items-center">
              <b-button
                variant="bg-white"
                size="sm"
                :disabled="row.item.transcribeJob === null || (row.item.transcribeJob !== null && row.item.transcribeJob.status !== 3)"
                @click="row.toggleDetails"
              >
                {{ getStatus(row.item.transcribeJob) === 'Finished' ? (row.detailsShowing ? 'Hide' : 'Show') : getStatus(row.item.transcribeJob) }}
              </b-button>
            </span>
          </div>
        </template>

        <template #cell(video)="data">
          <span class="text-nowrap">
            {{ data.value || '-' }}
          </span>
        </template>

        <template #cell(audio)="data">
          {{ data.value || '-' }}
        </template>
        <template #cell(status)="data">
          {{ data.value || '-' }}
        </template>
        <template #cell(progress)="data">
          {{ data.item && !isNotApplicable(data.item.status) ? data.value : '-' }}
        </template>
        <template #cell(logs)="data">
          <a @click="showLogs(data.item)">
            <GjIcon name="FileAlt" />
          </a>
        </template>
        <template #cell(restart)="data">
          <a @click="restartEncodingOutput(data.item)">
            <GjIcon name="Refresh" />
          </a>
        </template>
      </b-table>
    </div>

    <b-modal
      v-model="outputTimingModal"
      :title="
        outputTiming
          ? isHLS
            ? `${outputTiming.name} - ${outputTiming.nameModifier}`
            : `${outputTiming.nameModifier || outputTiming.name}`
          : 'Timing Logs'
      "
      centered
      ok-title="Close"
      ok-variant="bg-white"
      ok-only
      body-class="p-0"
      size="lg"
    >
      <OutputTimingLogs
        v-if="outputTiming"
        :id="outputTiming ? outputTiming.id : null"
      />
    </b-modal>
  </div>
</template>

<script>
import OutputTimingLogs from './OutputTimingLogs.vue'
import JobTranscriptions from './JobTranscriptions.vue'
import OutputMobileCard from '../components/OutputMobileCard.vue'

export default {
  name: 'OutputGroupSummary',
  filters: {
    urlLimit: val => (val.length > 70 ? `${val.slice(0, 70)}...` : val),
  },
  components: {
    OutputTimingLogs,
    JobTranscriptions,
    OutputMobileCard,
  },
  props: {
    isEnded: {
      type: Boolean,
      default: false,
    },
    destination: {
      type: String,
      default: '',
    },
    group: {
      type: Object,
      default: null,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      refreshing: false,
      outputTiming: null,
      outputTimingModal: false,
      fields: [
        { key: 'name', label: 'Output' },
        { key: 'transcriptions', label: 'Transcriptions' },
        { key: 'video', label: 'Video settings' },
        { key: 'audio', label: 'Audio settings' },
        { key: 'status', label: 'Status' },
        {
          key: 'progress',
          formatter(val) {
            return val !== null ? `${val}%` : '-'
          },
        },
        { key: 'logs', label: '' },
        { key: 'restart', label: 'Restart' },
      ],
    }
  },
  computed: {
    filteredFields() {
      const includeTranscriptions = this.isOutputMp3
      return this.fields.filter(field => field.key !== 'transcriptions' || includeTranscriptions)
    },
    computedTranscribePath() {
      const { domainName, path } = this.buildPathAndDomain(this.destination, this.group, this.extension)
      const masterFilePath = this.group.masterFile
      return domainName + path + masterFilePath
    },
    job() {
      return this.$store.state.jobs.job
    },
    tableData() {
      return this.group.outputs.map(o => ({
        id: o.id,
        name: o.name,
        nameModifier: o.nameModifier,
        video: o.videoCodec,
        audio: o.audioCodec,
        transcribeJob: o.transcribeJob,
        status: o.encodingOutputJob
          ? o.encodingOutputJob.statusDescription
          : null,
        progress: o.encodingOutputJob ? o.encodingOutputJob.progress : null,
        path: this.getOutputPath(o),
      }))
    },
    outputGroupPathComputed() {
      const { domainName, path } = this.buildPathAndDomain(this.destination, this.group, this.extension)
      return domainName + path + this.group.masterFile
    },
    isHLS() {
      return this.group.name === 'AppleHLS'
    },
    extension() {
      return process.env.VUE_APP_ENV === 'production' ? 'tech' : 'dev'
    },
    isOutputMp3() {
      return this.getOutputPath(this.group.outputs[0]).toLowerCase().endsWith('mp3')
    },
  },
  methods: {
    refresh() {
      this.refreshing = true
      this.$store
        .dispatch('jobs/refreshOutputGroupTweak', this.$route.params.name)
        .then(() => {
          this.refreshing = false
        })
        .catch(() => {
          this.refreshing = false
        })
    },
    getOutputPath(chunk) {
      const { domainName, path } = this.buildPathAndDomain(this.destination, this.group, this.extension)
      const chunkFileExtension = chunk.uploadedPath ? `${chunk.uploadedPath}/` : ''
      const masterFilePath = chunk.masterFile
      const chunkFilePath = domainName + path + chunkFileExtension + masterFilePath
      return chunk.masterFile ? chunkFilePath : ''
    },
    buildPathAndDomain(destination, group, extension) {
      let domainName = `https://blob.gjirafa.${extension}/`
      const path = `${destination}/${group.uploadedPath}/`
      if (!this.isNullOrEmpty(this.job.destinationEndpoint) && !this.job.destinationEndpoint?.includes('blob.gjirafa')) {
        domainName = this.job.destinationEndpoint
      }

      return { domainName, path }
    },
    showLogs(item) {
      this.outputTiming = item
      this.outputTimingModal = true
    },
    restartEncodingOutput(item) {
      const data = {
        outputId: item.id,
        jobId: this.$route.params.name,
      }
      this.confirmDelete(
        'Restart encoding output',
        'Are you sure you want to restart this encoding job?',
        'Restart',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('jobs/restartEncodingOutput', data)
            .then(() => {
              this.successToast('EncodingOutput was restarted!')
              this.refresh()
            })
            .catch(() => {
              this.errorToast('Something went wrong while restarting output.')
            })
        }
      })
    },
    isNotApplicable(status) {
      return status ? status.toLowerCase().includes('not applicable') : false
    },
    getStatus(transcribeJob) {
      if (transcribeJob === null) return 'Failed'
      if (transcribeJob.status === 1) return 'Pending'
      if (transcribeJob.status === 2) return 'Processing'
      if (transcribeJob.status === 3) return 'Finished'
      return 'Failed'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.output-group-path {
  margin-top: 4px;
}

.output-group {
  &__heading {
    min-width: 0;
    max-width: 100%;
  }

  &__empty {
    padding: 16px 0;
    font-size: 13px;
    color: $blue-800;
    text-align: center;
  }
}

// On phones the output group path is a long URL; keep it on one line so the
// group header stays compact and tappable.
@media (max-width: 991.98px) {
  .output-group__path-link {
    display: flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
  }

  .output-group__path-text {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
